import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabCSVImport.module.css';
import MetaHead from '../components/MetaHead';
import { formatNumber } from '../utils/format';

/* ============================================================
   TabCSVImport — onglet "Import CSV"
   ------------------------------------------------------------
   IDs DOM préservés (backend Express) :
     #csv-source-grid · #csv-dropzone · #csv-file-input
     #csv-preview · #csv-history-tbody · #csv-import-btn

   Callbacks :
     onImport({ source, file, mapping }) ← lancerImportCSV()
     onDownloadTemplate(source)          ← telechargerTemplate()
     onDeleteImport(id)                  ← supprimerImport()
   ============================================================ */

// SOURCES — la liste des intégrations supportées. Les `name` (marques) restent
// non-traduisibles. Les `desc` sont résolues en runtime via i18n.
const SOURCES = [
  { id: 'thefork',    color: '#5BC4BF', emoji: '🍴' },
  { id: 'zenchef',    color: '#FF6B35', emoji: '👨‍🍳' },
  { id: 'sevenrooms', color: '#7C3AED', emoji: '🥂' },
  { id: 'opentable',  color: '#DA3743', emoji: '🍽️' },
  { id: 'sumup',      color: '#0070F3', emoji: '💳' },
  { id: 'custom',     color: '#6B7280', emoji: '📄' },
];

function buildFmtBytes(t) {
  return function fmtBytes(b) {
    if (b < 1024) return t('csv_import.bytes_b', { count: b });
    if (b < 1024 * 1024) return t('csv_import.bytes_kb', { value: (b / 1024).toFixed(1) });
    return t('csv_import.bytes_mb', { value: (b / (1024 * 1024)).toFixed(2) });
  };
}

function getSourceName(t, sourceId) {
  // Les noms des outils tiers (TheFork, Zenchef, etc.) restent identiques en
  // toutes langues, mais on les passe quand même par i18n pour le cas "custom"
  // qui se traduit (CSV libre / Custom CSV / CSV personalizado / CSV libero).
  return t(`csv_import.sources.${sourceId}_name`, { defaultValue: sourceId });
}

function getSourceDesc(t, sourceId) {
  return t(`csv_import.sources.${sourceId}_desc`, { defaultValue: '' });
}

/* ============================================================
   SourceCard
============================================================ */
function SourceCard({ source, active, onSelect, t }) {
  return (
    <button
      type="button"
      className={`${styles.sourceCard} ${active ? styles.sourceCardActive : ''}`}
      onClick={() => onSelect(source.id)}
      style={active ? { '--src-color': source.color } : undefined}
    >
      <div className={styles.sourceIcon} style={{ background: `${source.color}1F`, color: source.color }}>
        <span>{source.emoji}</span>
      </div>
      <div className={styles.sourceBody}>
        <div className={styles.sourceName}>{getSourceName(t, source.id)}</div>
        <div className={styles.sourceDesc}>{getSourceDesc(t, source.id)}</div>
      </div>
      {active && <span className={styles.sourceCheck}>✓</span>}
    </button>
  );
}

/* ============================================================
   Dropzone
============================================================ */
function Dropzone({ file, onFile, onClear, t, fmtBytes }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  if (file) {
    return (
      <div className={styles.fileSelected}>
        <div className={styles.fileIcon}>📄</div>
        <div className={styles.fileBody}>
          <div className={styles.fileName}>{file.name}</div>
          <div className={styles.fileMeta}>{fmtBytes(file.size)} · CSV</div>
        </div>
        <button type="button" className={styles.fileClear} onClick={onClear} aria-label={t('csv_import.file_remove_label')}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      id="csv-dropzone"
      className={`${styles.dropzone} ${drag ? styles.dropzoneActive : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
    >
      <div className={styles.dropEmoji}>📥</div>
      <div className={styles.dropText}>
        <strong>{t('csv_import.drop_strong')}</strong>
        <span>{t('csv_import.drop_or')}<u>{t('csv_import.drop_browse')}</u></span>
      </div>
      <div className={styles.dropHint}>{t('csv_import.drop_hint')}</div>
      <input
        id="csv-file-input"
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
      />
    </div>
  );
}

/* ============================================================
   HistoryRow
============================================================ */
function HistoryRow({ entry, onDelete, t }) {
  const status = entry.status || 'success';
  const map = {
    success: { labelKey: 'csv_import.status_success', cls: styles.statSuccess },
    partial: { labelKey: 'csv_import.status_partial', cls: styles.statPartial },
    failed:  { labelKey: 'csv_import.status_failed',  cls: styles.statFailed  },
    pending: { labelKey: 'csv_import.status_pending', cls: styles.statPending },
  };
  const st = map[status] || map.success;
  const src = SOURCES.find((s) => s.id === entry.source);

  return (
    <tr>
      <td className={styles.tdSrc} data-label={t('csv_import.table_source')}>
        {src && (
          <span className={styles.srcChip} style={{ background: `${src.color}1A`, color: src.color }}>
            {src.emoji} {getSourceName(t, src.id)}
          </span>
        )}
        {!src && entry.source}
      </td>
      <td data-label={t('csv_import.table_file')}>{entry.fileName || '—'}</td>
      <td className={styles.tdNum} data-label={t('csv_import.table_lines')}>{entry.rowCount ?? '—'}</td>
      <td className={styles.tdNum} data-label={t('csv_import.table_imported')}>{entry.imported ?? '—'}</td>
      <td className={styles.tdDate} data-label={t('csv_import.table_date')}>{entry.date || '—'}</td>
      <td data-label={t('csv_import.table_status')}><span className={`${styles.statusPill} ${st.cls}`}>{t(st.labelKey)}</span></td>
      <td className={styles.tdActions} data-label="">
        {onDelete && (
          <button type="button" className={styles.linkBtn} onClick={() => onDelete(entry.id)}>
            {t('common.delete')}
          </button>
        )}
      </td>
    </tr>
  );
}

/* ============================================================
   TabCSVImport
============================================================ */
export default function TabCSVImport({
  history = [],
  onImport,
  onDownloadTemplate,
  onDeleteImport,
}) {
  const { t, i18n } = useTranslation('tabs');
  const lang = i18n.language || 'fr';
  const fmtBytes = useMemo(() => buildFmtBytes(t), [t]);
  const [source, setSource] = useState('thefork');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const selectedSrc = useMemo(() => SOURCES.find((s) => s.id === source), [source]);
  const selectedSrcName = selectedSrc ? getSourceName(t, selectedSrc.id) : 'CSV';

  const handleImport = useCallback(async () => {
    if (!file || !onImport) return;
    setImporting(true);
    try {
      await onImport({ source, file });
      setFile(null);
    } finally {
      setImporting(false);
    }
  }, [file, source, onImport]);

  const totalImported = history.reduce((acc, e) => acc + (e.imported || 0), 0);

  return (
    <>
      <MetaHead page="csv_import" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('csv_import.title_emoji')}</span>{t('csv_import.title')}</h1>
          <p>{t('csv_import.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          {selectedSrc && onDownloadTemplate && (
            <button type="button" className={styles.btnGhost} onClick={() => onDownloadTemplate(source)}>
              {t('csv_import.template_button', { source: selectedSrcName })}
            </button>
          )}
        </div>
      </header>

      {/* ===== Stat bar ===== */}
      <div className={styles.statBar}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('csv_import.stat_imports_label')}</span>
          <strong>{history.length}</strong>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('csv_import.stat_contacts_label')}</span>
          <strong>{formatNumber(totalImported, lang)}</strong>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('csv_import.stat_last_label')}</span>
          <strong>{history[0]?.date || '—'}</strong>
        </div>
      </div>

      {/* ===== Step 1: Source ===== */}
      <section className={styles.block}>
        <header className={styles.blockHead}>
          <span className={styles.blockStep}>1</span>
          <div>
            <h2>{t('csv_import.step_1_title')}</h2>
            <span className={styles.blockSub}>{t('csv_import.step_1_sub')}</span>
          </div>
        </header>
        <div id="csv-source-grid" className={styles.sourceGrid}>
          {SOURCES.map((s) => (
            <SourceCard key={s.id} source={s} active={source === s.id} onSelect={setSource} t={t} />
          ))}
        </div>
      </section>

      {/* ===== Step 2: File ===== */}
      <section className={styles.block}>
        <header className={styles.blockHead}>
          <span className={styles.blockStep}>2</span>
          <div>
            <h2>{t('csv_import.step_2_title')}</h2>
            <span className={styles.blockSub}>
              {t('csv_import.step_2_sub_part1')}<strong>{selectedSrcName}</strong>
            </span>
          </div>
        </header>
        <Dropzone file={file} onFile={setFile} onClear={() => setFile(null)} t={t} fmtBytes={fmtBytes} />

        {file && (
          <div className={styles.importFooter}>
            <button
              id="csv-import-btn"
              type="button"
              className={styles.btnPrimary}
              onClick={handleImport}
              disabled={importing}
            >
              {importing
                ? t('csv_import.btn_importing')
                : t('csv_import.btn_import', { source: selectedSrcName })}
            </button>
          </div>
        )}
      </section>

      {/* ===== History ===== */}
      <section className={styles.block}>
        <header className={styles.blockHead}>
          <span className={styles.blockStep}>📋</span>
          <div>
            <h2>{t('csv_import.history_title')}</h2>
            <span className={styles.blockSub}>{t('csv_import.history_count', { count: history.length })}</span>
          </div>
        </header>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('csv_import.table_source')}</th>
                <th>{t('csv_import.table_file')}</th>
                <th className={styles.thNum}>{t('csv_import.table_lines')}</th>
                <th className={styles.thNum}>{t('csv_import.table_imported')}</th>
                <th>{t('csv_import.table_date')}</th>
                <th>{t('csv_import.table_status')}</th>
                <th />
              </tr>
            </thead>
            <tbody id="csv-history-tbody">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.tableEmpty}>
                    {t('csv_import.table_empty')}
                  </td>
                </tr>
              ) : (
                history.map((e, i) => (
                  <HistoryRow key={e.id ?? i} entry={e} onDelete={onDeleteImport} t={t} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
